import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, CheckCircle2, Clock, X, RefreshCw, DollarSign, Loader2 } from 'lucide-react';
import { batchProcessingService, BatchJob } from '@/services/batch-processing-service';

interface EnhancedBatchProgressDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobId: string | null;
  jobType: 'token_refresh' | 'balance_sync' | 'pattern_refresh';
}

export function EnhancedBatchProgressDialog({ 
  isOpen, 
  onClose, 
  jobId, 
  jobType 
}: EnhancedBatchProgressDialogProps) {
  const [job, setJob] = useState<BatchJob | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    if (!jobId || !isOpen) return;

    setIsPolling(true);
    const pollInterval = setInterval(async () => {
      try {
        const jobStatus = await batchProcessingService.getBatchJobStatus(jobId);
        if (jobStatus) {
          setJob(jobStatus);
          
          // Stop polling if job is completed, failed, or cancelled
          if (['completed', 'failed', 'cancelled'].includes(jobStatus.status)) {
            setIsPolling(false);
            clearInterval(pollInterval);
          }
        }
      } catch (error) {
        console.error('Failed to poll job status:', error);
      }
    }, 1000); // Poll every 1 second for more responsive updates

    // Initial fetch
    batchProcessingService.getBatchJobStatus(jobId).then(setJob);

    return () => {
      clearInterval(pollInterval);
      setIsPolling(false);
    };
  }, [jobId, isOpen]);

  const handleCancel = async () => {
    if (jobId && job?.status === 'running') {
      try {
        await batchProcessingService.cancelBatchJob(jobId);
        setJob(prev => prev ? { ...prev, status: 'cancelled' } : null);
      } catch (error) {
        console.error('Failed to cancel job:', error);
      }
    }
  };

  const getPhaseIcon = (phase?: string) => {
    switch (phase) {
      case 'token_refresh':
        return <RefreshCw className="h-4 w-4 text-blue-500" />;
      case 'balance_sync':
        return <DollarSign className="h-4 w-4 text-green-500" />;
      case 'pattern_refresh':
        return <RefreshCw className="h-4 w-4 text-purple-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPhaseLabel = (phase?: string) => {
    switch (phase) {
      case 'token_refresh':
        return 'Refreshing Access Tokens';
      case 'balance_sync':
        return 'Syncing Account Balances';
      case 'pattern_refresh':
        return 'Analyzing Trading Patterns';
      default:
        return 'Processing';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-5 w-5 text-red-500" />;
      case 'cancelled':
        return <X className="h-5 w-5 text-gray-500" />;
      case 'running':
        return <Loader2 className="h-5 w-5 text-blue-500 animate-spin" />;
      default:
        return <Clock className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'failed': return 'destructive';
      case 'cancelled': return 'secondary';
      case 'running': return 'outline';
      default: return 'outline';
    }
  };

  const formatDuration = (startTime: string, endTime?: string) => {
    const start = new Date(startTime);
    const end = endTime ? new Date(endTime) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    return `${minutes}m ${seconds}s`;
  };

  const getCurrentOperationText = () => {
    if (!job) return '';
    
    if (job.status === 'running') {
      if (job.current_connection_name) {
        const operation = job.current_phase === 'token_refresh' ? 'Refreshing token for' : 
                         job.current_phase === 'balance_sync' ? 'Syncing balance for' : 
                         'Processing pattern for';
        return `${operation}: ${job.current_connection_name}`;
      }
      return getPhaseLabel(job.current_phase);
    }
    
    return '';
  };

  const getIndicatorColor = (status: string) => {
    switch (status) {
      case 'completed': return 'bg-green-500';
      case 'failed': return 'bg-red-500';
      case 'cancelled': return 'bg-gray-500';
      default: return 'bg-blue-500';
    }
  };

  if (!job) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Loading Job Status...</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <div className="animate-pulse space-y-4">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-8 bg-gray-200 rounded"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getStatusIcon(job.status)}
            {jobType === 'token_refresh' ? 'Token Refresh Progress' : 
             jobType === 'balance_sync' ? 'Balance Sync Progress' : 'Pattern Refresh Progress'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Status & Phase */}
          <div className="flex items-center justify-between">
            <Badge variant={getStatusBadgeVariant(job.status)}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </Badge>
            {job.current_phase && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                {getPhaseIcon(job.current_phase)}
                {getPhaseLabel(job.current_phase)}
              </div>
            )}
          </div>

          {/* Current Operation */}
          {job.status === 'running' && getCurrentOperationText() && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  <span className="text-sm font-medium">{getCurrentOperationText()}</span>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Progress Bar */}
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Overall Progress</span>
              <span>{job.progress_percentage}%</span>
            </div>
            <Progress 
              value={job.progress_percentage} 
              className="h-3"
              indicatorClassName={getIndicatorColor(job.status)}
            />
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-center">Processed</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-center text-green-600">
                  {job.processed_items}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-center">Total</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-center">
                  {job.total_items}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-center">Failed</CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="text-2xl font-bold text-center text-red-600">
                  {job.failed_items}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Duration */}
          <div className="text-center text-sm text-muted-foreground">
            <Clock className="h-4 w-4 inline mr-1" />
            Duration: {formatDuration(job.created_at, job.updated_at)}
          </div>

          {/* Error Message */}
          {job.error_message && (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-800">{job.error_message}</div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-2 justify-end">
            {job.status === 'running' && (
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleCancel}
              >
                Cancel
              </Button>
            )}
            <Button 
              variant={job.status === 'completed' ? 'default' : 'outline'}
              size="sm" 
              onClick={onClose}
            >
              {job.status === 'completed' ? 'Done' : 'Close'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
